import type { FC } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Grid, IconButton, Stack, Typography, TextField } from '@mui/material';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../components/base';
import { useConfirmDialog } from '../../../../../hooks/useConfirmDialog';
import { TypeLocation } from '../../../../../state/locationsForm';
import type { MapFormLocationProps } from '../model/useMapFormLocation';
import useMapFormLocation from '../model/useMapFormLocation';

export const MapFormLocation: FC<MapFormLocationProps> = ({ locationForm, onEndAction, goBack }) => {
  const { onSubmit, onDelete, form, control, isAddedLocation, validateTitle, isValid } = useMapFormLocation({
    locationForm,
    onEndAction,
  });
  const {
    formState: { errors },
  } = form;
  const { t } = useTranslation();
  const { showConfirm, confirmModal } = useConfirmDialog({ content: t('confirm-delete-location') });

  const handleDelete = async () => {
    const isOk = await showConfirm();
    if (isOk) onDelete();
  };

  const editTitle = locationForm.type === TypeLocation.New ? t('edit-new-location') : t('edit-location');
  const title = isAddedLocation ? t('add-new-location') : editTitle;

  return (
    <FormProvider {...form}>
      <Stack component='form' onSubmit={onSubmit} flexGrow={1} justifyItems='space-between'>
        <Stack>
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
            {goBack ? (
              <IconButton onClick={goBack} color='primary' size='large'>
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography variant='h4' sx={{ flex: 1 }}>
              {title}
            </Typography>
            {locationForm.canRemove && (
              <Button onClick={handleDelete} type='button' color='error' variant='contained'>
                {t('delete')}
              </Button>
            )}
          </Stack>

          <Grid container columnSpacing={1} rowSpacing={2} mt={2}>
            <Grid xs={12} item>
              <Controller
                name='title'
                rules={{
                  required: true,
                  validate: (value) => validateTitle(value) || (t('location-name-already-exists') as string),
                }}
                control={control}
                render={({ field }) => (
                  <TextField
                    placeholder='Title'
                    label={t('title')}
                    fullWidth
                    required
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    {...field}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} item>
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <TextField
                    placeholder='Description'
                    label={t('description')}
                    fullWidth
                    maxRows={5}
                    multiline
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>

        <Button
          fullWidth
          type='submit'
          sx={{ marginTop: 'auto', color: 'white' }}
          variant='contained'
          disabled={!isValid}
        >
          {title}
        </Button>
      </Stack>
      {confirmModal}
    </FormProvider>
  );
};
